import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CookieNotice } from "@/components/ui/cookie-notice";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./context/AuthContext";
import { AppLockProvider } from "./context/AppLockContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AccountSettingsProvider } from "./context/AccountSettingsContext";
import { BusinessSettingsProvider } from "./context/BusinessSettingsContext";
import { DashboardTabProvider } from "./context/DashboardTabContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { EmployeeProvider } from "./context/EmployeeContext";
import { EmployeeSelectionProvider } from "./context/EmployeeSelectionContext";
import { DimmingProvider } from "./context/DimmingContext";
import { PayrollHistoryProvider } from "./context/PayrollHistoryContext";
import { HistoricalEditProvider } from "./context/HistoricalEditContext";
import { BusinessProvider } from "./context/BusinessContext";
import { NotificationProvider } from "./context/NotificationContext";
import { UnifiedLoadingProvider } from "./context/UnifiedLoadingContext";
import { KeyboardShortcutsProvider } from "./context/KeyboardShortcutsContext";
import { CommandPaletteProvider } from "./context/CommandPaletteContext";
import { DndProvider } from 'react-dnd';
import { TouchDragBackend, TouchDragBackendOptions } from '@/lib/scheduling/touch-drag-backend';
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import SubmitReview from "./pages/SubmitReview";
import Auth from "./pages/Auth";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import NotFound from "./pages/NotFound";
import CreateBusiness from "./pages/CreateBusiness";
import BusinessDetails from "./pages/BusinessDetails";
import EditBusiness from "./pages/EditBusiness";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Contact from "./pages/Contact";
import FeaturesPage from "./pages/FeaturesPage";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import EmployeePortalPage from "./pages/EmployeePortalPage";
import FranchiseDashboard from "./pages/FranchiseDashboard";
import EmployeeProtectedRoute from "./components/auth/EmployeeProtectedRoute";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import SubscriptionRequiredRoute from "./components/auth/SubscriptionRequiredRoute";
import AdminProtectedRoute from "./components/auth/AdminProtectedRoute";
import AutoLogout from "./components/auth/AutoLogout";
import AppLockScreen from "./components/auth/AppLockScreen";
import AuthErrorBoundary from "./components/auth/AuthErrorBoundary";
import MFAGate from "./components/auth/MFAGate";
import { GlobalLayout } from "./components/layout/GlobalLayout";
// Admin pages
import AdminLayout from "./components/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import WaitlistManagement from "./pages/admin/WaitlistManagement";
import UsersManagement from "./pages/admin/UsersManagement";
import BusinessesOverview from "./pages/admin/BusinessesOverview";
import EmailTemplatesManagement from "./pages/admin/EmailTemplatesManagement";
import { ScrollRestoration } from "./components/ScrollRestoration";
// New consolidated feature pages
import { ReviewsPage } from "./pages/features/ReviewsPage";
import { LoyaltyRewardsPage } from "./pages/features/LoyaltyRewardsPage";
import { WorkforcePage } from "./pages/features/WorkforcePage";
import { MarketingPage } from "./pages/features/MarketingPage";
import { RecruitmentPage } from "./pages/features/RecruitmentPage";
import { AnalyticsPage } from "./pages/features/AnalyticsPage";
import { CRMPage } from "./pages/features/CRMPage";
import PassBuilderPage from "./pages/PassBuilderPage";
import EnhancedGetPass from "./pages/EnhancedGetPass";
import UpdatePassPage from "./pages/UpdatePassPage";
import KioskPage from "./pages/KioskPage";
import GetPass from "./pages/GetPass";
import JobApplication from "./pages/JobApplication";
import JobDetails from "./pages/JobDetails";
import PublicReviewForm from "./pages/PublicReviewForm";
import PreviewReviewForm from "./pages/PreviewReviewForm";
import CouponTokenRedemption from "./pages/CouponTokenRedemption";
import SignagePlayer from "./pages/signage/Player";
import Pricing from "./pages/Pricing";
import SelectPlan from "./pages/SelectPlan";
import ManagePlan from "./pages/ManagePlan";
import Privacy from "./pages/Privacy";
import BusinessPrivacy from "./pages/BusinessPrivacy";
import Terms from "./pages/Terms";
import UnsubscribeSuccess from "./components/public/UnsubscribeSuccess";
import UnsubscribeAlready from "./components/public/UnsubscribeAlready";
import UnsubscribeError from "./components/public/UnsubscribeError";

// Pricing route - always accessible
const PricingRoute = () => {
  return <Pricing />;
};

// Create a client instance for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: false, // Prevent refetch when browser tab becomes visible (Supabase reconnects)
      refetchOnMount: false, // Use cached data when components remount (prevents refetch on tab switch)
    },
  },
});

// Home route - just shows the Index page
const HomeRoute = () => {
  return <Index />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <DndProvider backend={TouchDragBackend} options={TouchDragBackendOptions}>
      <AuthErrorBoundary>
        <AuthProvider>
          <MFAGate>
          <AppLockProvider>
          <EmployeeProvider>
            <EmployeeSelectionProvider>
              <SubscriptionProvider>
                <PayrollHistoryProvider>
                  <ThemeProvider>
                    <UnifiedLoadingProvider>
                    <Sonner />
                    <AccountSettingsProvider>
                      <BusinessSettingsProvider>
                        <DashboardTabProvider>
                        <DimmingProvider>
                          <HistoricalEditProvider>
                            <TooltipProvider>
                            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                              {/* ScrollRestoration disabled - was causing scroll reset on tab switch */}
                              {/* <ScrollRestoration /> */}
                              <CookieNotice />
                              <AutoLogout />
                              <AppLockScreen />
                              <BusinessProvider>
                              <NotificationProvider>
                              <KeyboardShortcutsProvider>
                              <CommandPaletteProvider>
                              <GlobalLayout>
                              <Routes>
                                <Route path="/" element={<HomeRoute />} />
                                <Route path="/dashboard" element={<SubscriptionRequiredRoute><Dashboard /></SubscriptionRequiredRoute>} />
                                <Route path="/submit-review" element={<SubmitReview />} />
                                <Route path="/sign-in" element={<SignIn />} />
                                <Route path="/sign-up" element={<SignUp />} />
                                <Route path="/select-plan" element={<ProtectedRoute><SelectPlan /></ProtectedRoute>} />
                                <Route path="/manage-plan" element={<ProtectedRoute><ManagePlan /></ProtectedRoute>} />
                                <Route path="/waitlist" element={<Navigate to="/sign-up" replace />} />
                                <Route path="/auth" element={<Navigate to="/sign-in" replace />} />
                                <Route path="/signup" element={<Navigate to="/sign-up" replace />} />
                                <Route path="/create-business" element={<SubscriptionRequiredRoute><CreateBusiness /></SubscriptionRequiredRoute>} />
                                <Route path="/business/:businessId" element={<BusinessDetails />} />
                                <Route path="/edit-business/:businessId" element={<EditBusiness />} />
                                <Route path="/pass-builder" element={<PassBuilderPage />} />
                                <Route path="/blog" element={<Blog />} />
                                <Route path="/blog/:slug" element={<BlogPost />} />
                                <Route path="/contact" element={<Contact />} />
                                <Route path="/features" element={<FeaturesPage />} />
                                <Route path="/pricing" element={<PricingRoute />} />
                                <Route path="/privacy" element={<Privacy />} />
                                <Route path="/privacy/:businessId" element={<BusinessPrivacy />} />
                                <Route path="/terms" element={<Terms />} />
                                <Route path="/employee-dashboard" element={<SubscriptionRequiredRoute><EmployeeDashboard /></SubscriptionRequiredRoute>} />
                                <Route path="/employee-portal" element={<EmployeeProtectedRoute><EmployeePortalPage /></EmployeeProtectedRoute>} />
                                <Route path="/portfolio" element={<SubscriptionRequiredRoute><FranchiseDashboard /></SubscriptionRequiredRoute>} />
                                {/* New consolidated feature pages */}
                                <Route path="/features/reviews" element={<ReviewsPage />} />
                                <Route path="/features/loyalty" element={<LoyaltyRewardsPage />} />
                                <Route path="/features/workforce" element={<WorkforcePage />} />
                                <Route path="/features/marketing" element={<MarketingPage />} />
                                <Route path="/features/recruitment" element={<RecruitmentPage />} />
                                <Route path="/features/analytics" element={<AnalyticsPage />} />
                                <Route path="/features/crm" element={<CRMPage />} />

                                {/* Redirects from old routes to new consolidated pages */}
                                <Route path="/features/shift-management" element={<Navigate to="/features/workforce" replace />} />
                                <Route path="/features/employee-management" element={<Navigate to="/features/workforce" replace />} />
                                <Route path="/features/bulk-mail" element={<Navigate to="/features/marketing" replace />} />
                                <Route path="/features/tags" element={<Navigate to="/features/crm" replace />} />
                                <Route path="/features/coupons" element={<Navigate to="/features/loyalty" replace />} />
                                <Route path="/get-pass" element={<EnhancedGetPass />} />
                                <Route path="/update-pass" element={<UpdatePassPage />} />
                                <Route path="/kiosk" element={<KioskPage />} />
                                <Route path="/signage/player" element={<SignagePlayer />} />
                                <Route path="/job-details" element={<JobDetails />} />
                                <Route path="/job-application" element={<JobApplication />} />
                                <Route path="/review/:formId" element={<PublicReviewForm />} />
                                <Route path="/review/:formId/preview" element={<PreviewReviewForm />} />
                                <Route path="/coupon-token/:token" element={<CouponTokenRedemption />} />
                                <Route path="/email-unsubscribe/successful" element={<UnsubscribeSuccess />} />
                                <Route path="/email-unsubscribe/already-unsubscribed" element={<UnsubscribeAlready />} />
                                <Route path="/email-unsubscribe/:type" element={<UnsubscribeError />} />
                                {/* Admin routes */}
                                <Route path="/admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
                                  <Route index element={<AdminDashboard />} />
                                  <Route path="waitlist" element={<WaitlistManagement />} />
                                  <Route path="users" element={<UsersManagement />} />
                                  <Route path="businesses" element={<BusinessesOverview />} />
                                  <Route path="email-templates" element={<EmailTemplatesManagement />} />
                                </Route>
                                <Route path="*" element={<NotFound />} />
                              </Routes>
                              </GlobalLayout>
                              </CommandPaletteProvider>
                              </KeyboardShortcutsProvider>
                              </NotificationProvider>
                              </BusinessProvider>
                            </BrowserRouter>
                            </TooltipProvider>
                          </HistoricalEditProvider>
                        </DimmingProvider>
                      </DashboardTabProvider>
                      </BusinessSettingsProvider>
                    </AccountSettingsProvider>
                    </UnifiedLoadingProvider>
                  </ThemeProvider>
                </PayrollHistoryProvider>
              </SubscriptionProvider>
            </EmployeeSelectionProvider>
          </EmployeeProvider>
          </AppLockProvider>
          </MFAGate>
        </AuthProvider>
      </AuthErrorBoundary>
    </DndProvider>
  </QueryClientProvider>
);

export default App;
